import { Fonts, Spacings } from '@styles/index';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import H6 from '@base/H6/H6';
import H7 from '@base/H7/H7';
import { useDispatch, useSelector } from 'react-redux';
import { QUIZ_CONTENT_STATE } from '@constants/index';
import Iframe from '@comp/FileUpload/Iframe';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { updateEducatorSlides } from '@actions/templates';
import { getImage } from '@utils/imageHelper';

const Wrapper = styled.div`
  border-top: 1px dashed var(--color-divider);
  margin-top: ${Spacings.SPACING_6B};
  padding-top: ${Spacings.SPACING_6B};
  display: flex;
  & > :not(:last-child) {
    margin-right: ${({ $isEducator }) =>
      $isEducator ? `${Spacings.SPACING_13B}` : `${Spacings.SPACING_27B}`};
  }
  & > :last-child {
    margin-right: ${({ $isEducator }) =>
      $isEducator ? `${Spacings.SPACING_12B}` : 0};
  }
`;

const Flex = styled.div`
  display: flex;
`;

const UnderlineButton = styled(H7)`
  width: fit-content;
  cursor: pointer;
  border-bottom: 1px dashed var(--color-divider);
  line-height: ${Fonts.BUTTON_LINE_HEIGHT};
  padding-bottom: ${Spacings.SPACING_1B};
  margin-top: ${Spacings.SPACING_2B};
`;

const Image = styled.div`
  background-color: ${({ $bgColor }) => $bgColor};
  background-image: ${({ $bgImage }) => `url(${$bgImage})`};
  height: 48px;
  width: 48px;
  padding: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_6};
  background-repeat: no-repeat;
  background-position: center;
  background-size: 22px;
  margin-right: ${Spacings.SPACING_3B};
`;

const getQuizStateInfo = (contentState, sessionUID, source) => {
  if (!sessionUID) return {};
  switch (contentState) {
    case QUIZ_CONTENT_STATE.QUESTION_COMPLETED:
      return {
        label: 'VIEW QUESTIONS',
        url: `/educator/test/review/${sessionUID}/?exitFrom=top&source=${source}`
      };

    default:
      return {
        label: 'ADD QUESTIONS',
        url: `/educator/test/edit/${sessionUID}?source=${source}`
      };
  }
};

const ClassMetaData = ({
  learnerNotes,
  educatorSlides,
  quizUID,
  canView,
  goalUID,
  topicGroups,
  isEducator,
  className
}) => {
  const quizState = useSelector(
    ({ templates }) => templates.addEditTemplate.quizStates[quizUID]
  );
  const router = useRouter();
  const dispatch = useDispatch();
  const [driveMode, setDriveMode] = useState('');
  let action = getQuizStateInfo(quizState, quizUID, router.asPath);
  if (canView)
    action = {
      label: 'VIEW QUESTIONS',
      url: `/educator/test/review/${quizUID}/?exitFrom=top&source=${router.asPath}`
    };

  const finishEditing = (id, data) =>
    dispatch(updateEducatorSlides(data, educatorSlides.uid));

  const handleEducatorSlides = () => {
    if (canView) setDriveMode('view');
    else setDriveMode('edit');
  };

  const slides = Array.isArray(educatorSlides)
    ? educatorSlides[0]
    : educatorSlides;

  if (!slides?.uid && !learnerNotes?.url && !quizUID) return null;

  return (
    <Wrapper $isEducator={isEducator} className={className}>
      {slides?.uid && (
        <Flex>
          <Image
            $bgColor="var(--color-i-pink-2)"
            $bgImage={getImage('pdf.svg')}
            alt="pdf download"
          />
          <div>
            <H6>Educator slides</H6>
            <UnderlineButton
              color="var(--color-i-blue)"
              onClick={handleEducatorSlides}
            >
              {canView ? 'VIEW' : 'OPEN EDITOR'}
              {['view', 'edit'].includes(driveMode) && (
                <Iframe
                  id="educatorSlides"
                  mode={driveMode}
                  setMode={setDriveMode}
                  file={slides}
                  finishUpload={finishEditing}
                  goalUID={goalUID}
                  topicGroups={topicGroups}
                  isEducator={isEducator}
                />
              )}
            </UnderlineButton>
          </div>
        </Flex>
      )}
      {learnerNotes?.url && (
        <Flex>
          <Image
            $bgColor="var(--color-i-light-green)"
            $bgImage={getImage('stats/lessons.svg')}
            alt="statistics lesson"
          />
          <div>
            <H6>Learner notes</H6>
            <UnderlineButton
              color="var(--color-i-blue)"
              onClick={() => window.open(learnerNotes.url, '_blank')}
            >
              VIEW
            </UnderlineButton>
          </div>
        </Flex>
      )}
      {quizUID && (
        <Flex>
          <Image
            $bgColor="var(--color-i-orange-3)"
            $bgImage={getImage('assignment.svg')}
            alt="assignment"
          />
          <div>
            <H6>Practice</H6>
            <UnderlineButton
              color="var(--color-i-blue)"
              onClick={
                () => window.open(`${window.location.origin}${action.url}`)
                // eslint-disable-next-line react/jsx-curly-newline
              }
            >
              {action.label}
            </UnderlineButton>
          </div>
        </Flex>
      )}
    </Wrapper>
  );
};

export default ClassMetaData;

ClassMetaData.propTypes = {
  learnerNotes: PropTypes.objectOf(PropTypes.any),
  educatorSlides: PropTypes.objectOf(PropTypes.any),
  quizUID: PropTypes.string,
  canView: PropTypes.bool,
  goalUID: PropTypes.string,
  topicGroups: PropTypes.string,
  isEducator: PropTypes.bool,
  className: PropTypes.string
};

ClassMetaData.defaultProps = {
  quizUID: '',
  learnerNotes: {},
  educatorSlides: {},
  canView: false,
  goalUID: '',
  topicGroups: '',
  isEducator: false,
  className: ''
};
